import { createMachine } from 'xstate';
import {
  isClaimWithMailboxDeliveryWarning,
  isClaimOutsideOfDeadline,
  isWarehouseDeliveryDateRequired,
  isNonManual,
  isClaimWithMissingTrackingData,
  isInvestigationClaim,
  isWholeDamageClaim,
  isPartialDamageClaim,
  isMissingItemClaim,
  isClaimsCenter,
  isShipmentDelivered,
  isCashOnDeliveryClaim,
  isNewInvestigationClaim,
  isNewWholeDamageClaim,
  isNewCashOnDeliveryClaim,
  isNewPartialDamageClaim,
  isNewMissingItemClaim,
  isClaimsCenterAndNonManual,
  isManualClaim,
  isAdditionalInfoRequired,
  isDorSigningLinkStepEnabled,
} from '../../parcel-finder/claims/claimsHelpers';

const commonSteps = {
  'easy-shipping-info': {
    on: {
      NEXT: 'check-additional-info-step',
    },
  },
  'check-additional-info-step': {
    always: [
      {
        target: 'invoice-step',
        cond: isAdditionalInfoRequired,
      },
      {
        target: 'invoice-step-final',
      },
    ],
  },
  'invoice-step': {
    on: { NEXT: 'additional-info-step' },
  },
  'invoice-step-final': {
    type: 'final',
  },
  'additional-info-step': {
    type: 'final',
  },
  'deadline-shipping-info': {
    on: {
      NEXT: 'manual-deadline-check',
    },
  },
  'manual-deadline-check': {
    on: {
      NEXT: [
        {
          target: 'easy-shipping-info',
          cond: isClaimsCenter,
        },
        {
          target: 'check-additional-info-step',
        },
      ],
    },
  },
  'outside-of-deadline': {
    on: {
      NEXT: [
        {
          target: 'easy-shipping-info',
          cond: isClaimsCenter,
        },
        {
          target: 'check-additional-info-step',
        },
      ],
    },
  },
};

const commonCheckShipmentTargets = [
  {
    target: 'deadline-shipping-info',
    cond: isClaimWithMissingTrackingData,
  },
  {
    target: 'outside-of-deadline',
    cond: isClaimOutsideOfDeadline,
  },
  {
    target: 'easy-shipping-info',
    cond: isClaimsCenterAndNonManual,
  },
  {
    target: 'check-additional-info-step',
    cond: isNonManual,
  },
  {
    target: 'deadline-shipping-info',
    cond: isManualClaim,
  },
];

const damageAndMissingItem = {
  'check-shipment': {
    always: [
      {
        target: 'warehouse-delivery-confirmation-info',
        cond: isWarehouseDeliveryDateRequired,
      },
      ...commonCheckShipmentTargets,
    ],
  },
  'warehouse-delivery-confirmation-info': {
    on: {
      NEXT: 'manual-deadline-check',
    },
  },
  ...commonSteps,
};

export default createMachine({
  key: 'submit-claim',
  strict: true,
  initial: 'choose-claim-type',
  states: {
    'choose-claim-type': {
      on: {
        SELECT_DAMAGE: 'choose-damage-type',
        SELECT_INVESTIGATION: 'choose-entry-point',
        SELECT_MISSING_ITEM: 'choose-entry-point',
        SELECT_CASH_ON_DELIVERY: 'choose-entry-point',
      },
    },
    'choose-damage-type': {
      on: {
        SELECT_PARTIAL_DAMAGE: 'choose-entry-point',
        SELECT_WHOLE_DAMAGE: 'choose-entry-point',
      },
    },
    'choose-entry-point': {
      always: [
        {
          target: 'search-claim-info',
          cond: isClaimsCenter,
        },
        {
          target: 'dispatch-claim-type',
        },
      ],
    },
    'search-claim-info': {
      on: {
        NEXT: 'dispatch-claim-type',
      },
    },
    'draft-loading': {
      on: {
        NEXT: 'dispatch-claim-type',
      },
    },
    'dispatch-claim-type': {
      always: [
        {
          target: 'newInvestigation',
          cond: isNewInvestigationClaim,
        },
        {
          target: 'newWholeDamage',
          cond: isNewWholeDamageClaim,
        },
        {
          target: 'newPartialDamage',
          cond: isNewPartialDamageClaim,
        },
        {
          target: 'newMissingItem',
          cond: isNewMissingItemClaim,
        },
        {
          target: 'investigation',
          cond: isInvestigationClaim,
        },
        {
          target: 'whole-damage',
          cond: isWholeDamageClaim,
        },
        {
          target: 'partial-damage',
          cond: isPartialDamageClaim,
        },
        {
          target: 'missing-item',
          cond: isMissingItemClaim,
        },
        {
          target: 'new-cash-on-delivery',
          cond: isNewCashOnDeliveryClaim,
        },
        {
          target: 'cash-on-delivery',
          cond: isCashOnDeliveryClaim,
        },
      ],
    },
    investigation: {
      initial: 'check-shipment',
      states: {
        'check-shipment': {
          always: [
            {
              target: 'investigation-delivered-to-mailbox',
              cond: isClaimWithMailboxDeliveryWarning,
            },
            {
              target: 'deadline-shipping-info',
              cond: isClaimWithMissingTrackingData,
            },
            {
              target: 'outside-of-deadline',
              cond: isClaimOutsideOfDeadline,
            },
            {
              target: 'shipping-info',
              cond: isNonManual,
            },
            {
              target: 'deadline-shipping-info',
            },
          ],
        },
        'investigation-delivered-to-mailbox': {
          on: {
            NEXT: [
              {
                target: 'outside-of-deadline',
                cond: isClaimOutsideOfDeadline,
              },
              {
                target: 'shipping-info',
                cond: isNonManual,
              },
              {
                target: 'deadline-shipping-info',
              },
            ],
          },
        },
        'deadline-shipping-info': {
          on: {
            NEXT: 'manual-deadline-check',
          },
        },
        'manual-deadline-check': {
          on: {
            NEXT: 'shipping-info',
          },
        },
        'outside-of-deadline': {
          on: {
            NEXT: 'shipping-info',
          },
        },
        'shipping-info': {
          on: {
            NEXT: [
              {
                target: 'investigation-delivery-info',
                cond: isShipmentDelivered,
              },
              {
                target: 'invoice-info',
              },
            ],
          },
        },
        'investigation-delivery-info': {
          on: {
            NEXT: 'invoice-info',
          },
        },
        'invoice-info': {
          type: 'final',
        },
      },
    },
    newInvestigation: {
      initial: 'check-shipment',
      states: {
        'check-shipment': {
          always: [
            {
              target: 'investigation-delivered-to-mailbox',
              cond: isClaimWithMailboxDeliveryWarning,
            },
            ...commonCheckShipmentTargets,
          ],
        },
        'investigation-delivered-to-mailbox': {
          on: {
            NEXT: [
              {
                target: 'outside-of-deadline',
                cond: isClaimOutsideOfDeadline,
              },
              {
                target: 'deadline-shipping-info',
                cond: (claim) => !isNonManual(claim),
              },
              {
                target: 'easy-shipping-info',
                cond: isClaimsCenter,
              },
              {
                target: 'check-additional-info-step',
              },
            ],
          },
        },
        ...commonSteps,
        /* We need to override the invoice-step (the non-final one) for the flow where a DOR is required:
         * This is to insert an intermediate step for generating the DOR signing link, if DOR signing linke is enabled.
         * This override only applies to this specific claim type — none of the others require a DOR.
         * We don’t need to override invoice-step-final,
         * because that step is only used for non-delivered investigation claims.*/
        'invoice-step': {
          on: {
            NEXT: [
              {
                cond: isDorSigningLinkStepEnabled,
                target: 'create-dor-step',
              },
              {
                target: 'additional-info-step',
              },
            ],
          },
        },
        'create-dor-step': {
          on: { NEXT: 'additional-info-step' },
        },
      },
    },
    'whole-damage': {
      initial: 'check-shipment',
      states: {
        'check-shipment': {
          always: [
            {
              target: 'warehouse-delivery-confirmation-info',
              cond: isWarehouseDeliveryDateRequired,
            },
            {
              target: 'outside-of-deadline',
              cond: isClaimOutsideOfDeadline,
            },
            {
              target: 'deadline-shipping-info',
              cond: isClaimWithMissingTrackingData,
            },
            {
              target: 'shipping-info',
              cond: isNonManual,
            },
            {
              target: 'deadline-shipping-info',
            },
          ],
        },
        'deadline-shipping-info': {
          on: {
            NEXT: 'manual-deadline-check',
          },
        },
        'warehouse-delivery-confirmation-info': {
          on: {
            NEXT: 'manual-deadline-check',
          },
        },
        'manual-deadline-check': {
          on: {
            NEXT: 'shipping-info',
          },
        },
        'outside-of-deadline': {
          on: {
            NEXT: 'shipping-info',
          },
        },
        'shipping-info': {
          on: {
            NEXT: 'whole-damage-info',
          },
        },
        'whole-damage-info': {
          on: {
            NEXT: 'invoice-info',
          },
        },
        'invoice-info': {
          type: 'final',
        },
      },
    },
    newWholeDamage: {
      initial: 'check-shipment',
      states: damageAndMissingItem,
    },

    'partial-damage': {
      initial: 'check-shipment',
      states: {
        'check-shipment': {
          always: [
            {
              target: 'warehouse-delivery-confirmation-info',
              cond: isWarehouseDeliveryDateRequired,
            },
            {
              target: 'outside-of-deadline',
              cond: isClaimOutsideOfDeadline,
            },
            {
              target: 'deadline-shipping-info',
              cond: isClaimWithMissingTrackingData,
            },
            {
              target: 'shipping-info',
              cond: isNonManual,
            },
            {
              target: 'deadline-shipping-info',
            },
          ],
        },
        'deadline-shipping-info': {
          on: {
            NEXT: 'manual-deadline-check',
          },
        },
        'warehouse-delivery-confirmation-info': {
          on: {
            NEXT: 'manual-deadline-check',
          },
        },
        'manual-deadline-check': {
          on: {
            NEXT: 'shipping-info',
          },
        },
        'outside-of-deadline': {
          on: {
            NEXT: 'shipping-info',
          },
        },
        'shipping-info': {
          on: {
            NEXT: 'partial-damage-info',
          },
        },
        'partial-damage-info': {
          on: {
            NEXT: 'invoice-info',
          },
        },
        'invoice-info': {
          type: 'final',
        },
      },
    },
    newPartialDamage: {
      initial: 'check-shipment',
      states: damageAndMissingItem,
    },
    'missing-item': {
      initial: 'check-shipment',
      states: {
        'check-shipment': {
          always: [
            {
              target: 'warehouse-delivery-confirmation-info',
              cond: isWarehouseDeliveryDateRequired,
            },
            {
              target: 'outside-of-deadline',
              cond: isClaimOutsideOfDeadline,
            },
            {
              target: 'deadline-shipping-info',
              cond: isClaimWithMissingTrackingData,
            },
            {
              target: 'shipping-info',
              cond: isNonManual,
            },
            {
              target: 'deadline-shipping-info',
            },
          ],
        },
        'deadline-shipping-info': {
          on: {
            NEXT: 'manual-deadline-check',
          },
        },
        'warehouse-delivery-confirmation-info': {
          on: {
            NEXT: 'manual-deadline-check',
          },
        },
        'manual-deadline-check': {
          on: {
            NEXT: 'shipping-info',
          },
        },
        'outside-of-deadline': {
          on: {
            NEXT: 'shipping-info',
          },
        },
        'shipping-info': {
          on: {
            NEXT: 'missing-item-info',
          },
        },
        'missing-item-info': {
          on: {
            NEXT: 'invoice-info',
          },
        },
        'invoice-info': {
          type: 'final',
        },
      },
    },
    newMissingItem: {
      initial: 'check-shipment',
      states: damageAndMissingItem,
    },
    'cash-on-delivery': {
      initial: 'check-shipment',
      states: {
        'check-shipment': {
          always: [
            {
              target: 'deadline-shipping-info',
              cond: isClaimWithMissingTrackingData,
            },
            {
              target: 'outside-of-deadline',
              cond: isClaimOutsideOfDeadline,
            },
            {
              target: 'shipping-info',
              cond: isNonManual,
            },
            {
              target: 'deadline-shipping-info',
            },
          ],
        },
        'deadline-shipping-info': {
          on: {
            NEXT: 'manual-deadline-check',
          },
        },
        'manual-deadline-check': {
          on: {
            NEXT: 'shipping-info',
          },
        },
        'outside-of-deadline': {
          on: {
            NEXT: 'shipping-info',
          },
        },
        'shipping-info': {
          on: {
            NEXT: [
              {
                target: 'proof-of-payment',
                cond: (claim) => !isNonManual(claim),
              },
              {
                target: 'proof-of-payment',
                cond: isClaimWithMissingTrackingData,
              },
              {
                target: 'cash-on-delivery-payment-info',
              },
            ],
          },
        },
        'proof-of-payment': {
          on: {
            NEXT: 'cash-on-delivery-payment-info',
          },
        },
        'cash-on-delivery-payment-info': {
          type: 'final',
        },
      },
    },
    'new-cash-on-delivery': {
      initial: 'check-shipment',
      states: {
        'check-shipment': {
          always: [...commonCheckShipmentTargets],
        },
        ...commonSteps,
      },
    },
  },
});
